import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import QuickEditImage from '@/components/quickedit/QuickEditImage';
import ImageWithFallback from '@/components/ui/ImageWithFallback';
import SectionPhotoCredit from '@/components/homepage/SectionPhotoCredit';

interface ImageBannerProps {
  section: any;
}

export default function ImageBanner({ section }: ImageBannerProps) {
  const content = section.content ?? {};
  const overlayText = content.overlay_text || section.title;
  const ctaText = content.cta_text;
  const ctaLink = content.cta_link;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  return (
    <section className="relative overflow-hidden">
      <div className="aspect-[21/9] sm:aspect-[21/7] relative">
        <QuickEditImage
          src={content.image_url}
          onSave={(url) => mutation.mutateAsync({ content: { ...section.content, image_url: url } })}
          label="Banner image"
          targetWidth={2100}
          targetHeight={900}
          specKey="image_banner.image_url"
        >
          <ImageWithFallback
            src={content.image_url}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
          <SectionPhotoCredit content={content} />
        </QuickEditImage>
        <div className="absolute inset-0 overlay-dark" aria-hidden="true" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 max-w-3xl">
            {overlayText && (
              <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                {isActive ? (
                  <QuickEditInline
                    value={overlayText}
                    onSave={(val) => mutation.mutateAsync({
                      content: { ...section.content, overlay_text: val },
                    })}
                    label="Banner overlay text"
                    as="span"
                    className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white"
                  />
                ) : overlayText}
              </p>
            )}
            {section.subtitle && (
              <p className="mt-2 font-display italic text-white/90 text-lg">
                {isActive ? (
                  <QuickEditInline
                    value={section.subtitle}
                    onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
                    label="Banner subtitle"
                    as="span"
                    className="font-display italic text-white/90 text-lg"
                  />
                ) : section.subtitle}
              </p>
            )}
            {ctaText && ctaLink && (
              <Link
                to={ctaLink}
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-cta-fg/90 px-6 py-3 text-sm font-medium text-cta hover:bg-cta-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {ctaText}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
